/**
 * Cliente HTTP del backend.
 *
 * Autenticación según el modo (`auth/keycloak.ts`):
 * - **keycloak**: cada petición lleva `Authorization: Bearer <token>`, renovado
 *   antes de caducar. Un 401 significa sesión caducada: se vuelve al login.
 * - **local** (desarrollo): usuario y contraseña por cabecera (`X-Usuario` /
 *   `X-Clave-Usuario`).
 *
 * La sesión (empresa elegida y perfil) vive en `sessionStorage`, no en
 * `localStorage`: en un puesto de salidas compartido no debe quedar nada tras
 * cerrar el navegador. Con Keycloak no se guarda ninguna contraseña.
 *
 * El endpoint público `/d/{token}` NO se consume desde aquí: lo abre el navegador
 * del agente de inspección directamente desde el QR, sin pasar por esta capa.
 */

import type {
  EmpresaSeleccion,
  DatosEmision,
  DatosModificacion,
  DatosModificacionTransporte,
  DecaCrear,
  DecaDetalle,
  DecaResumen,
  Empresa,
  ErrorApi,
  CanalEnvio,
  ConfigCanales,
  Direccion,
  UsoDireccion,
  EstadoDeca,
  ListadoDeca,
  RespuestaEmision,
  RespuestaEnvioEnlace,
  ResumenCarga,
  Sugerencia,
  Transportista,
} from './tipos'

import { conKeycloak, tokenVigente, volverAlLogin } from '../auth/keycloak'

const CLAVE_ALMACEN = 'dox.credenciales'

export interface Credenciales {
  usuario: string
  clave: string
  empresaId: string
}

/** Rol, permisos y empresas del usuario, con los que la interfaz decide qué mostrar. */
export interface Perfil {
  username: string
  nombre: string
  /** Administrador de la instalación (ajustes globales): perfil admin en todas las empresas. */
  es_administrador: boolean
  /** Todos los permisos que tiene en alguna empresa. */
  permisos: string[]
  /** Empresas a las que puede acceder (un administrador, a todas las activas). */
  empresas: string[]
  /** Permisos en cada empresa: con Keycloak pueden variar de una a otra. */
  permisos_por_empresa?: Record<string, string[]>
  autenticacion?: 'keycloak' | 'local'
}

export type Permiso =
  | 'salidas'
  | 'documentos'
  | 'transportistas'
  | 'direcciones'
  | 'empresas'
  | 'configuracion'
  | 'usuarios'

/** Permisos que se marcan a mano en la gestión local de usuarios (modo desarrollo). */
export type PermisoLocal = Exclude<Permiso, 'configuracion' | 'usuarios'>

export type RolUsuario = 'ADMINISTRADOR' | 'USUARIO'

/** Ficha de usuario para la pantalla de gestión (sin la contraseña). */
export interface UsuarioAdmin {
  usuario_id: number
  username: string
  nombre: string
  rol: RolUsuario
  puede_salidas: boolean
  puede_documentos: boolean
  puede_transportistas: boolean
  puede_direcciones: boolean
  puede_empresas: boolean
  empresas: string[]
  activo: boolean
}

export interface UsuarioCrear {
  username: string
  nombre: string
  password: string
  rol: RolUsuario
  puede_salidas: boolean
  puede_documentos: boolean
  puede_transportistas: boolean
  puede_direcciones: boolean
  puede_empresas: boolean
  empresas: string[]
}

/** Datos de acceso del usuario nuevo; rol, permisos y empresas salen del original. */
export interface UsuarioCopiar {
  username: string
  nombre: string
  password: string
}

export interface UsuarioActualizar {
  nombre?: string
  password?: string
  rol?: RolUsuario
  puede_salidas?: boolean
  puede_documentos?: boolean
  puede_transportistas?: boolean
  puede_direcciones?: boolean
  puede_empresas?: boolean
  empresas?: string[]
  activo?: boolean
}

export class ErrorPeticion extends Error {
  constructor(
    readonly estado: number,
    readonly clave: string,
    mensaje: string,
  ) {
    super(mensaje)
    this.name = 'ErrorPeticion'
  }

  /** True si el problema son las credenciales y hay que volver a la pantalla de acceso. */
  get esNoAutorizado(): boolean {
    return this.estado === 401
  }
}

// --- Credenciales ----------------------------------------------------------

export function guardarCredenciales(credenciales: Credenciales): void {
  sessionStorage.setItem(CLAVE_ALMACEN, JSON.stringify(credenciales))
}

export function leerCredenciales(): Credenciales | null {
  try {
    const bruto = sessionStorage.getItem(CLAVE_ALMACEN)
    return bruto ? (JSON.parse(bruto) as Credenciales) : null
  } catch {
    return null
  }
}

export function olvidarCredenciales(): void {
  sessionStorage.removeItem(CLAVE_ALMACEN)
}

const CLAVE_PERFIL = 'dox.perfil'

export function guardarPerfil(perfil: Perfil): void {
  sessionStorage.setItem(CLAVE_PERFIL, JSON.stringify(perfil))
}

export function leerPerfil(): Perfil | null {
  try {
    const bruto = sessionStorage.getItem(CLAVE_PERFIL)
    return bruto ? (JSON.parse(bruto) as Perfil) : null
  } catch {
    return null
  }
}

export function olvidarPerfil(): void {
  sessionStorage.removeItem(CLAVE_PERFIL)
}

async function cabeceras(credenciales: Credenciales | null): Promise<Record<string, string>> {
  if (conKeycloak) {
    const token = await tokenVigente()
    return token ? { Authorization: `Bearer ${token}` } : {}
  }
  if (!credenciales) return {}
  return {
    'X-Usuario': credenciales.usuario,
    'X-Clave-Usuario': credenciales.clave,
  }
}

/** Con Keycloak, un 401 es una sesión caducada o revocada: de vuelta al login. */
function trasRespuesta(respuesta: Response): void {
  if (respuesta.status === 401 && conKeycloak) volverAlLogin()
}

// --- Petición base ---------------------------------------------------------

interface Opciones {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  parametros?: Record<string, string | number | boolean | undefined | null>
  cuerpo?: unknown
  formulario?: FormData
  /** Sobrescribe las credenciales guardadas (salidas emite con su propia clave). */
  credenciales?: Credenciales | null
}

async function peticion<T>(ruta: string, opciones: Opciones = {}): Promise<T> {
  // `credenciales: null` (el login) significa "ninguna", no "las guardadas".
  const credenciales =
    opciones.credenciales !== undefined ? opciones.credenciales : leerCredenciales()
  const url = new URL(ruta, window.location.origin)

  if (credenciales?.empresaId && !ruta.startsWith('/api/v1/maestros/empresas')) {
    url.searchParams.set('empresa_id', credenciales.empresaId)
  }
  for (const [nombre, valor] of Object.entries(opciones.parametros ?? {})) {
    if (valor !== undefined && valor !== null && valor !== '') {
      url.searchParams.set(nombre, String(valor))
    }
  }

  const cabecerasPeticion: Record<string, string> = await cabeceras(credenciales)
  let cuerpo: BodyInit | undefined
  if (opciones.formulario) {
    cuerpo = opciones.formulario
  } else if (opciones.cuerpo !== undefined) {
    cabecerasPeticion['Content-Type'] = 'application/json'
    cuerpo = JSON.stringify(opciones.cuerpo)
  }

  const respuesta = await fetch(url.toString(), {
    method: opciones.metodo ?? 'GET',
    headers: cabecerasPeticion,
    body: cuerpo,
  })

  if (!respuesta.ok) {
    trasRespuesta(respuesta)
    throw await construirError(respuesta)
  }
  if (respuesta.status === 204) {
    return undefined as T
  }
  return (await respuesta.json()) as T
}

async function construirError(respuesta: Response): Promise<ErrorPeticion> {
  let clave = 'ERROR'
  let detalle = `Error ${respuesta.status}`
  try {
    const cuerpo = (await respuesta.json()) as ErrorApi & { detail?: unknown }
    if (cuerpo.clave) clave = cuerpo.clave
    if (cuerpo.detalle) {
      detalle = cuerpo.detalle
    } else if (typeof cuerpo.detail === 'string') {
      detalle = cuerpo.detail
    } else if (Array.isArray(cuerpo.detail)) {
      // Error de validación de FastAPI: se muestra el primero, legible.
      const primero = cuerpo.detail[0] as { loc?: unknown[]; msg?: string }
      const campo = Array.isArray(primero?.loc) ? primero.loc.slice(-1)[0] : ''
      detalle = [campo, primero?.msg].filter(Boolean).join(': ')
      clave = 'VALIDACION'
    }
  } catch {
    /* respuesta sin JSON: se queda el mensaje genérico */
  }
  return new ErrorPeticion(respuesta.status, clave, detalle)
}

/** URL de descarga de un fichero servido por el backend, con las credenciales en query. */
function urlDescarga(ruta: string, parametros: Record<string, string> = {}): string {
  const credenciales = leerCredenciales()
  const url = new URL(ruta, window.location.origin)
  if (credenciales?.empresaId) url.searchParams.set('empresa_id', credenciales.empresaId)
  for (const [nombre, valor] of Object.entries(parametros)) {
    url.searchParams.set(nombre, valor)
  }
  return url.toString()
}

/**
 * Descarga un fichero autenticado y lo abre.
 *
 * Se hace con fetch y no con un enlace directo porque las credenciales van en
 * cabeceras, y un `<a href>` no las envía.
 */
async function descargarFichero(ruta: string): Promise<Blob> {
  const credenciales = leerCredenciales()
  const respuesta = await fetch(urlDescarga(ruta), { headers: await cabeceras(credenciales) })
  if (!respuesta.ok) {
    trasRespuesta(respuesta)
    throw await construirError(respuesta)
  }
  return await respuesta.blob()
}

// --- API -------------------------------------------------------------------

export const api = {
  salud: () => peticion<{ servicio: string; version: string; entorno: string }>('/api/salud'),

  /**
   * Empresas activas para el selector de la pantalla de acceso.
   *
   * No exige credenciales: hay que poder elegir empresa antes de entrar, y el
   * puesto de salidas no tiene la clave de back-office. Devuelve solo codigo y
   * razon social.
   */
  empresasParaSeleccion: () =>
    peticion<EmpresaSeleccion[]>(
      '/api/v1/maestros/empresas/seleccion',
    ),

  empresas: () =>
    peticion<Empresa[]>('/api/v1/maestros/empresas', { parametros: { solo_activas: true } }),

  actualizarEmpresa: (empresaId: string, datos: Partial<Empresa>) =>
    peticion<Empresa>(`/api/v1/maestros/empresas/${empresaId}`, {
      metodo: 'PATCH',
      cuerpo: datos,
    }),

  /** Sube (o reemplaza) el logo de la empresa. Se imprime en el PDF del DeCA. */
  subirLogoEmpresa: (empresaId: string, fichero: File) => {
    const formulario = new FormData()
    formulario.append('fichero', fichero)
    return peticion<Empresa>(`/api/v1/maestros/empresas/${empresaId}/logo`, {
      metodo: 'PUT',
      formulario,
    })
  },

  borrarLogoEmpresa: (empresaId: string) =>
    peticion<Empresa>(`/api/v1/maestros/empresas/${empresaId}/logo`, { metodo: 'DELETE' }),

  /** Bytes del logo de la empresa, para previsualizarlo (credenciales por cabecera). */
  logoEmpresa: (empresaId: string) =>
    descargarFichero(`/api/v1/maestros/empresas/${empresaId}/logo`),

  transportistas: (opciones: { texto?: string; soloPendientes?: boolean } = {}) =>
    peticion<Transportista[]>('/api/v1/maestros/transportistas', {
      parametros: {
        texto: opciones.texto,
        solo_pendientes_revision: opciones.soloPendientes,
      },
    }),

  crearTransportista: (datos: {
    nif: string
    razon_social: string
    email_contacto?: string
    telefono_contacto?: string
    codigo_erp?: string
  }) =>
    peticion<Transportista>('/api/v1/maestros/transportistas', {
      metodo: 'POST',
      cuerpo: datos,
    }),

  actualizarTransportista: (id: number, datos: Partial<Transportista>) =>
    peticion<Transportista>(`/api/v1/maestros/transportistas/${id}`, {
      metodo: 'PATCH',
      cuerpo: datos,
    }),

  // --- Direcciones (libreta de origen y destino) ---

  direcciones: (
    opciones: { texto?: string; soloPendientes?: boolean; uso?: UsoDireccion } = {},
  ) =>
    peticion<Direccion[]>('/api/v1/maestros/direcciones', {
      parametros: {
        texto: opciones.texto,
        uso: opciones.uso,
        solo_pendientes_revision: opciones.soloPendientes,
      },
    }),

  crearDireccion: (datos: {
    codigo?: string | null
    uso?: UsoDireccion
    denominacion?: string
    direccion: string
    cp?: string
    poblacion?: string
    provincia?: string
    pais?: string
    email_contacto?: string | null
    telefono_contacto?: string | null
  }) => peticion<Direccion>('/api/v1/maestros/direcciones', { metodo: 'POST', cuerpo: datos }),

  actualizarDireccion: (id: number, datos: Partial<Direccion>) =>
    peticion<Direccion>(`/api/v1/maestros/direcciones/${id}`, {
      metodo: 'PATCH',
      cuerpo: datos,
    }),

  /**
   * Alta de un borrador desde cero, sin pasar por el Excel (apdo. 8; permiso
   * `documentos`). Nace sin PDF y sin URL; se emite luego desde salidas. El
   * `empresa_id` lo añade `peticion` desde las credenciales.
   */
  crearBorrador: (datos: DecaCrear) =>
    peticion<DecaDetalle>('/api/v1/deca', { metodo: 'POST', cuerpo: datos }),

  /**
   * Corrige un borrador (solo en estado BORRADOR). No crea versión: aún no hay
   * PDF ni URL publicados. Un emitido se cambia con `modificar` (apdo. Quinto).
   */
  editarBorrador: (uuid: string, datos: DecaCrear) =>
    peticion<DecaDetalle>(`/api/v1/deca/${uuid}`, { metodo: 'PUT', cuerpo: datos }),

  /**
   * Autocompletado de un campo del alta manual a partir de datos ya existentes
   * (transportista, origen, destino, naturaleza). Se llama a partir de 3
   * caracteres; devuelve valores que 'contienen' el texto, acotados a la empresa.
   */
  sugerencias: (campo: string, texto: string, credenciales?: Credenciales) =>
    peticion<Sugerencia[]>('/api/v1/deca/sugerencias', {
      parametros: { campo, texto },
      credenciales,
    }),

  listarDeca: (
    opciones: {
      estado?: EstadoDeca | ''
      fecha?: string
      texto?: string
      pagina?: number
      porPagina?: number
    },
    credenciales?: Credenciales,
  ) =>
    peticion<ListadoDeca>('/api/v1/deca', {
      parametros: {
        estado: opciones.estado || undefined,
        fecha: opciones.fecha,
        texto: opciones.texto,
        pagina: opciones.pagina ?? 1,
        por_pagina: opciones.porPagina ?? 50,
      },
      credenciales,
    }),

  detalleDeca: (uuid: string) => peticion<DecaDetalle>(`/api/v1/deca/${uuid}`),

  /** Borradores pendientes de emitir. Exige credenciales de salidas. */
  pendientes: (texto: string, credenciales?: Credenciales) =>
    peticion<DecaResumen[]>('/api/v1/deca/pendientes', {
      parametros: { texto },
      credenciales,
    }),

  emitir: (uuid: string, datos: DatosEmision, credenciales?: Credenciales) =>
    peticion<RespuestaEmision>(`/api/v1/deca/${uuid}/emitir`, {
      metodo: 'POST',
      cuerpo: datos,
      credenciales,
    }),

  /**
   * Documentos ya emitidos, para el cambio de matrícula desde salidas cuando el
   * transportista cambia de vehículo tras emitir. Lo puede consultar `salidas`.
   */
  emitidos: (texto: string, credenciales?: Credenciales) =>
    peticion<DecaResumen[]>('/api/v1/deca/emitidos', {
      parametros: { texto },
      credenciales,
    }),

  /**
   * Cambio acotado de los datos del transporte (matrículas y/o transportista) que
   * puede hacer el puesto de salidas tras emitir (apdo. Quinto, método 1). La
   * pantalla lo pre-rellena con lo vigente y envía el conjunto; el backend solo
   * marca como sustituido lo que cambia. Mantiene la misma URL y el mismo QR, y
   * re-enlaza el maestro de transportistas si el transportista cambió.
   */
  cambiarTransporte: (
    uuid: string,
    datos: DatosModificacionTransporte,
    credenciales?: Credenciales,
  ) =>
    peticion<RespuestaEmision>(`/api/v1/deca/${uuid}/modificar-transporte`, {
      metodo: 'POST',
      cuerpo: datos,
      credenciales,
    }),

  registrarEntrega: (
    uuid: string,
    medio: 'IMPRESO' | 'ELECTRONICO',
    credenciales?: Credenciales,
  ) =>
    peticion<DecaDetalle>(`/api/v1/deca/${uuid}/entregado`, {
      metodo: 'POST',
      parametros: { medio },
      credenciales,
    }),

  /**
   * Envía al móvil del conductor el enlace del documento por SMS o WhatsApp
   * (apdo. 11.3). La pasarela está apagada por defecto: si no está configurada,
   * responde `enviado=false` con el motivo, y salidas conserva el enlace y el QR.
   * Registra la entrega ELECTRONICO cuando el envío se produce.
   */
  enviarEnlace: (
    uuid: string,
    canal: CanalEnvio,
    telefono?: string,
    credenciales?: Credenciales,
  ) =>
    peticion<RespuestaEnvioEnlace>(`/api/v1/deca/${uuid}/enviar-enlace`, {
      metodo: 'POST',
      parametros: { canal, telefono },
      credenciales,
    }),

  /** Qué canales de envío del enlace están activados (cualquier usuario). */
  canales: (credenciales?: Credenciales) =>
    peticion<ConfigCanales>('/api/v1/config/canales', { credenciales }),

  /** Activa o desactiva los canales de envío (solo administradores). */
  fijarCanales: (datos: ConfigCanales) =>
    peticion<ConfigCanales>('/api/v1/config/canales', { metodo: 'PUT', cuerpo: datos }),

  /** Borrador del siguiente camión de la misma referencia, con los mismos datos. */
  clonar: (uuid: string) =>
    peticion<DecaDetalle>(`/api/v1/deca/${uuid}/clonar`, { metodo: 'POST' }),

  modificar: (uuid: string, datos: DatosModificacion) =>
    peticion<RespuestaEmision>(`/api/v1/deca/${uuid}/modificar`, {
      metodo: 'POST',
      cuerpo: datos,
    }),

  anular: (uuid: string, motivo: string) =>
    peticion<DecaDetalle>(`/api/v1/deca/${uuid}/anular`, {
      metodo: 'POST',
      cuerpo: { motivo },
    }),

  finalizar: (uuid: string, finalizadoEn?: string) =>
    peticion<DecaDetalle>(`/api/v1/deca/${uuid}/finalizar`, {
      metodo: 'POST',
      cuerpo: { finalizado_en: finalizadoEn ?? null },
    }),

  reenviarCorreo: (uuid: string) =>
    peticion<{ enviado: boolean; error: string | null }>(
      `/api/v1/deca/${uuid}/reenviar-correo`,
      { metodo: 'POST' },
    ),

  subirExcel: (fichero: File, confirmarRecarga = false) => {
    const formulario = new FormData()
    formulario.append('fichero', fichero)
    formulario.append('confirmar_recarga', String(confirmarRecarga))
    // `empresa_id` viaja por query (lo añade `peticion` desde las credenciales):
    // así el control de acceso por empresa del backend cubre también la subida.
    return peticion<ResumenCarga>('/api/v1/cargas', { metodo: 'POST', formulario })
  },

  cargas: () => peticion<ResumenCarga[]>('/api/v1/cargas'),

  pdfDeca: (uuid: string) => descargarFichero(`/api/v1/deca/${uuid}/pdf`),

  informeCarga: (cargaId: number) => descargarFichero(`/api/v1/cargas/${cargaId}/informe`),

  // --- Acceso y gestión de usuarios ---------------------------------------

  /** Valida usuario y contraseña y devuelve el perfil (rol y permisos). */
  login: (usuario: string, clave: string) =>
    peticion<Perfil>('/api/v1/auth/login', {
      metodo: 'POST',
      cuerpo: { username: usuario, password: clave },
      // El login no lleva cabeceras de sesión: son justo las que se están validando.
      credenciales: null,
    }),

  /** Perfil vigente del usuario autenticado (para refrescar permisos). */
  perfil: (credenciales?: Credenciales) =>
    peticion<Perfil>('/api/v1/usuarios/yo', { credenciales }),

  usuarios: () => peticion<UsuarioAdmin[]>('/api/v1/usuarios'),

  crearUsuario: (datos: UsuarioCrear) =>
    peticion<UsuarioAdmin>('/api/v1/usuarios', { metodo: 'POST', cuerpo: datos }),

  copiarUsuario: (id: number, datos: UsuarioCopiar) =>
    peticion<UsuarioAdmin>(`/api/v1/usuarios/${id}/copiar`, { metodo: 'POST', cuerpo: datos }),

  actualizarUsuario: (id: number, datos: UsuarioActualizar) =>
    peticion<UsuarioAdmin>(`/api/v1/usuarios/${id}`, { metodo: 'PATCH', cuerpo: datos }),

  borrarUsuario: (id: number) =>
    peticion<void>(`/api/v1/usuarios/${id}`, { metodo: 'DELETE' }),
}

/** Abre un blob en una pestaña nueva; para imprimir el DeCA desde salidas. */
export function abrirBlob(blob: Blob): Window | null {
  const url = URL.createObjectURL(blob)
  const ventana = window.open(url, '_blank')
  // Se libera con retraso: si se revoca de inmediato, la pestaña nueva no llega
  // a cargarlo.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
  return ventana
}

/** Descarga un blob con nombre de fichero. */
export function guardarBlob(blob: Blob, nombre: string): void {
  const url = URL.createObjectURL(blob)
  const enlace = document.createElement('a')
  enlace.href = url
  enlace.download = nombre
  document.body.appendChild(enlace)
  enlace.click()
  enlace.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
